import MockedCentralProtocolJson from '../../../../app/assets/vsd-central-os-interface-protocol.json';
import { IKeyValueRepository } from '../../../repositories/key-value/key-value-repository';
import { makeEnvironment } from '../../../../environments/make-environment';
import { LocationId } from '../../../../main/translate/location-id/location-id';
import { RepositoryKey } from '../../../../models/common/repository/repository-key';
import { EventTopic } from '../../../../models/common/event-topic/event-topic';
import { IEventService } from '../event/event-service';
import { ILogService } from '../log/log-service';
import { ITranslateService } from '../translate/translate-service';
import {
  AppConfig,
  CentralSoftwareConfigProtocol,
  createDefaultAppConfig,
  IConfigService,
  PartialDeep,
} from './config-service';

export class ConfigServiceImpl implements IConfigService {
  public readonly config: AppConfig;

  private environment = makeEnvironment();
  private loadPromise?: Promise<AppConfig>;
  private principalEntityTaxId: string | null = null;
  private currentEntityTaxId: string | null = null;

  constructor(
    private logService: ILogService,
    private eventService: IEventService,
    private translateService: ITranslateService,
    private keyValueRepository: IKeyValueRepository,
    initialConfig?: AppConfig,
  ) {
    this.config = initialConfig ?? createDefaultAppConfig();
  }

  async init(): Promise<AppConfig> {
    if (!this.loadPromise) {
      this.loadPromise = this.loadAndApplyProtocol();
    }

    return this.loadPromise;
  }

  async reload(): Promise<AppConfig> {
    this.loadPromise = this.loadAndApplyProtocol();
    return this.loadPromise;
  }

  async applyProtocol(
    protocol: CentralSoftwareConfigProtocol,
  ): Promise<AppConfig> {
    const softwareConfig = this.findSoftwareConfig(protocol);
    const nextConfig = this.cloneConfig(softwareConfig?.settings);

    this.currentEntityTaxId = this.normalizeTaxId(protocol?.entity?.taxId);
    this.principalEntityTaxId = this.normalizeTaxId(
      softwareConfig?.principalEntity,
    );
    this.resetConfig(this.config, createDefaultAppConfig());
    this.mergeConfig(this.config, nextConfig);

    await this.translateService.init(this.getLanguage());
    this.eventService.emit(EventTopic.CONFIG_CHANGED, this.config);

    return this.config;
  }

  getLanguage(): LocationId {
    return this.config.general.language || 'pt-BR';
  }

  getTimezone(): string {
    return this.config.general.timezone || 'America/Sao_Paulo';
  }

  getPrincipalEntityTaxId(): string | null {
    return this.principalEntityTaxId;
  }

  getCurrentEntityTaxId(): string | null {
    return this.currentEntityTaxId;
  }

  isPrincipalEntityTaxId(taxId?: string | null): boolean {
    const normalizedTaxId = this.normalizeTaxId(taxId ?? undefined);

    if (!normalizedTaxId || !this.principalEntityTaxId) {
      return false;
    }

    return normalizedTaxId === this.principalEntityTaxId;
  }

  isCurrentEntityPrincipal(): boolean {
    if (!this.currentEntityTaxId || !this.principalEntityTaxId) {
      return false;
    }

    return this.currentEntityTaxId === this.principalEntityTaxId;
  }

  private async loadAndApplyProtocol(): Promise<AppConfig> {
    try {
      await this.logService.info(
        'ConfigServiceImpl.loadAndApplyProtocol -> start',
      );

      const protocol = await this.loadProtocolSource();
      return await this.applyProtocol(protocol);
    } catch (error) {
      await this.logService.error(
        'ConfigServiceImpl.loadAndApplyProtocol -> error',
        error,
      );

      this.currentEntityTaxId = null;
      this.principalEntityTaxId = null;
      this.resetConfig(this.config, createDefaultAppConfig());
      await this.translateService.init('pt-BR');
      this.eventService.emit(EventTopic.CONFIG_CHANGED, this.config);

      return this.config;
    } finally {
      await this.logService.info(
        'ConfigServiceImpl.loadAndApplyProtocol -> end',
      );
    }
  }

  private async loadProtocolSource(): Promise<CentralSoftwareConfigProtocol> {
    const assetUrl = new URL(
      'assets/vsd-central-os-interface-protocol.json',
      window.document.baseURI,
    ).toString();

    try {
      const selectedEntity = await this.keyValueRepository.get(
        RepositoryKey.SELECTED_ENTITY,
      );
      const entityId = selectedEntity?.id?.toString().trim();

      if (!entityId) {
        throw new Error('Selected entity not found');
      }

      const authService = await this.getAuthService();

      return (await authService.httpCentral.get(
        `/entity/${entityId}/configuration`,
        {
          _ts: Date.now(),
        },
      )) as CentralSoftwareConfigProtocol;
    } catch (remoteError) {
      await this.logService.warn(
        'ConfigServiceImpl.loadProtocolSource -> using bundled fallback',
        remoteError,
      );

      try {
        const response = await fetch(assetUrl, { cache: 'no-store' });

        if (!response.ok) {
          throw new Error(
            `Unable to load central config asset: ${response.status}`,
          );
        }

        return (await response.json()) as CentralSoftwareConfigProtocol;
      } catch (fallbackError) {
        await this.logService.warn(
          'ConfigServiceImpl.loadProtocolSource -> using bundled fallback import',
          fallbackError,
        );
        return MockedCentralProtocolJson as CentralSoftwareConfigProtocol;
      }
    }
  }

  private async getAuthService() {
    const { makeAuthService } = await import('../../auth/auth-service-factory');
    return makeAuthService();
  }

  private findSoftwareConfig(
    protocol: CentralSoftwareConfigProtocol,
  ) {
    const softwareId = this.environment.API_CENTRAL_SOFTWARE_ID.trim();
    const softwareList = protocol?.solution?.software || [];
    return softwareList.find(
      (software) => software.id?.trim() === softwareId,
    );
  }

  private cloneConfig(config?: PartialDeep<AppConfig>): PartialDeep<AppConfig> {
    if (!config || Array.isArray(config) || typeof config !== 'object') {
      return {};
    }

    return JSON.parse(JSON.stringify(config)) as PartialDeep<AppConfig>;
  }

  private normalizeTaxId(taxId?: string): string | null {
    const digits = (taxId || '').replace(/\D/g, '').trim();
    return digits || null;
  }

  private resetConfig(target: AppConfig, source: AppConfig): void {
    this.replaceObject(target, source);
  }

  private mergeConfig(target: AppConfig, source: PartialDeep<AppConfig>): void {
    this.mergeObject(target, source);
  }

  private replaceObject(
    target: Record<string, any>,
    source: Record<string, any>,
  ): void {
    Object.keys(target).forEach((key) => {
      delete target[key];
    });

    this.mergeObject(target, source);
  }

  private mergeObject(
    target: Record<string, any>,
    source: Record<string, any>,
  ): void {
    Object.entries(source || {}).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        target[key] = JSON.parse(JSON.stringify(value));
        return;
      }

      if (value && typeof value === 'object') {
        const currentTarget =
          target[key] &&
          typeof target[key] === 'object' &&
          !Array.isArray(target[key])
            ? target[key]
            : {};

        target[key] = currentTarget;
        this.mergeObject(currentTarget, value as Record<string, any>);
        return;
      }

      if (value !== undefined) {
        target[key] = value;
      }
    });
  }
}
